import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CalendarToday from '@material-ui/icons/CalendarToday';
import { Field } from 'formik';
import ReactDatePicker from 'react-datepicker';
import { isDriver, getDateLocale } from "globalutils/fmUtils";
import { UI_DATE_LOWER_FORMAT } from 'globalconfig/constants/static';

// Lazy-loaded components based on layout
const layout = window?.$environment?.CURRENT_LAYOUT;
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/Label/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);

const RenderComponent = ({
  values,
  handleEdit,
  errors,
  isNotValid,
  validateForm,
  customProps,
  submitAction
}) => {
  const { i18n } = useTranslation();

  // Define form fields
  const fields = {
    driverId: {
      type: 'select',
      field: 'driverId',
      placeholder: i18n.t('common.driver'),
      label: layout === 'SpireonNew' ? 'Select User' : ''
    },
    date: {
      type: 'date',
      field: 'date',
      placeholder: UI_DATE_LOWER_FORMAT,
      label: layout === 'SpireonNew' ? 'Date' : ''
    }
  };

  // Render error message if API status has an error
  const renderErrorMessage = () => (
    customProps.hasApiStatus?.errorMessage && (
      <Message
        type="error"
        message={i18n.t(customProps.hasApiStatus.errorMessage)}
      />
    )
  );

  // Render driver selection field
  const renderDriverField = () => (
    !isDriver() && (
      <FormStyle.default.FieldsWrapper className="vertical" width="32%">
        <Label variant="body1">{fields.driverId.label}</Label>
        <FormStyle.default.TextWrapper>
          <Field
            name={fields.driverId.field}
            as={Select}
            placeholderText={fields.driverId.placeholder || ''}
            value={values.driverId}
            type={fields.driverId.label}
            suggestions={customProps.drivers}
            onChange={(event) => {
              handleEdit(event, fields.driverId);
              customProps.setFormValues({
                ...customProps.formValues,
                driverId: event.value,
                driverName: event.label
              });
              customProps.fetchLogBookView({
                apiId: 'fetch',
                data: { data: { driverId: event.value } }
              });

              if (event.vehicleId) {
                customProps.fetchLogbookBreadcrumb({
                  apiId: customProps.fetchLogbookBreadcrumbApiId,
                  data: {
                    id: event.vehicleId,
                    day: values.date || customProps.currentDate
                  }
                });
              }
            }}
          />
          {errors['driverId'] && !isNotValid && (
            <Message type="error" message={errors['driverId']} />
          )}
        </FormStyle.default.TextWrapper>
      </FormStyle.default.FieldsWrapper>
    )
  );

  // Render date picker field
  const renderDateField = () => (
    <FormStyle.default.FieldsWrapper className="vertical verticalCal" width="32%">
      <Label variant="body1">{fields.date.label}</Label>
      <FormStyle.default.TextWrapper>
        <Field
          name={fields.date.field}
          as={ReactDatePicker}
          locale={getDateLocale()}
          placeholderText={fields.date.placeholder || ''}
          value={values.date}
          type={fields.date.label}
          showPopperArrow={false}
          showTimeSelect={fields.date.isTimeNeeded}
          showDisabledMonthNavigation
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          includeDates={customProps.dates}
          highlightDates={customProps.unSignedDates}
          onChange={(event) => handleEdit(event, fields.date)}
        />
        {layout === 'SpireonNew' && <CalendarToday />}
        {errors['date'] && !isNotValid && (
          <Message type="error" message={errors['date']} />
        )}
      </FormStyle.default.TextWrapper>
    </FormStyle.default.FieldsWrapper>
  );

  // Render submit button
  const renderSubmitButton = () => (
    <FormStyle.default.ButtonsWrapper className="vertical-button">
      <Button
        type="submit"
        label={i18n.t('common.view')}
        onClick={(e) => {
          const validateResponse = validateForm();
          validateResponse.then((data) => {
            submitAction(data);
          });
        }}
      />
    </FormStyle.default.ButtonsWrapper>
  );

  return (
    <>
      {renderErrorMessage()}
      {renderDriverField()}
      {renderDateField()}
      {renderSubmitButton()}
    </>
  );
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  hasApiStatus: PropTypes.object,
  validateForm: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default RenderComponent;